type Clipboard = NonNullable<Window['hibroClipboard']>;

/**
 * Browser implementation of the system-clipboard facade (见 16 / 18).
 *
 * The desktop counterpart is the preload `hibroClipboard`, which routes through
 * the main process's native `clipboard` on purpose: from a context menu the
 * renderer's `navigator.clipboard` frequently fails silently, because it wants
 * document focus and a fresh user gesture (image writes via `ClipboardItem` most
 * of all). A browser has no way around that, so this is the same API with a
 * weaker guarantee rather than a different one — every failure is logged instead
 * of swallowed, and callers already treat `false` as "try something else".
 *
 * `writeImagePath` is **absent**, not stubbed: it takes an OS path, which a
 * browser can neither produce nor read. Its absence is how callers know to fall
 * back to copying the rendered element (见 18 能力缺失即降级).
 */
export function createWebClipboard(
  log: (level: 'warn' | 'info', message: string) => void,
): Clipboard {
  function fail(action: string, err: unknown): false {
    log(
      'warn',
      `clipboard: ${action} failed (${err instanceof Error ? err.message : String(err)})`,
    );
    return false;
  }

  return {
    async writeText(text) {
      const value = text ?? '';
      if (!value) {
        return false;
      }
      try {
        await navigator.clipboard.writeText(value);
        return true;
      } catch (err) {
        return fail('writeText', err);
      }
    },
    async readText() {
      try {
        return await navigator.clipboard.readText();
      } catch (err) {
        fail('readText', err);
        return '';
      }
    },
    async writeImage(bytes) {
      if (!bytes || bytes.byteLength === 0) {
        return false;
      }
      try {
        // `Uint8Array<ArrayBufferLike>` might in principle be backed by a
        // `SharedArrayBuffer`, which `BlobPart` excludes; ours never is.
        const blob = new Blob([bytes as BlobPart], { type: 'image/png' });
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob }),
        ]);
        return true;
      } catch (err) {
        return fail('writeImage', err);
      }
    },
    async readImage() {
      try {
        for (const item of await navigator.clipboard.read()) {
          const type = item.types.find((value) => value.startsWith('image/'));
          if (type) {
            const blob = await item.getType(type);
            return new Uint8Array(await blob.arrayBuffer());
          }
        }
        return null;
      } catch (err) {
        fail('readImage', err);
        return null;
      }
    },
    async clear() {
      // No `clipboard.clear()` in the browser; writing an empty string is the
      // closest equivalent and is what the one caller (a "clear" action) means.
      try {
        await navigator.clipboard.writeText('');
      } catch (err) {
        fail('clear', err);
      }
    },
  };
}
